import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import AdminService from '../../../services/adminService';

/**
 * Translation Manager Component
 *
 * Provides interface for managing translations:
 * - Browse translation categories
 * - Edit translations in multiple languages
 * - Add new translation keys
 * - Mark translations for review
 */
const TranslationManager = () => {
  const { t, availableLanguages } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadTranslations(selectedCategory.id);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const data = await AdminService.getTranslationCategories();
      setCategories(data);
      if (data.length > 0 && !selectedCategory) {
        setSelectedCategory(data[0]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTranslations = async (categoryId) => {
    try {
      const data = await AdminService.getTranslationKeys(categoryId);
      setTranslations(data);
    } catch (err) {
      console.error('Error loading translations:', err);
    }
  };

  const updateTranslation = async (keyId, languageId, value) => {
    try {
      await AdminService.updateTranslation(keyId, languageId, value);
      await loadTranslations(selectedCategory.id);
    } catch (err) {
      console.error('Error updating translation:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">
          {t('admin.translations.title', 'Translation Management')}
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          {t('admin.translations.subtitle', 'Manage translations for all supported languages')}
        </p>
      </div>

      {/* Category Tabs */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${selectedCategory?.id === category.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
              >
                {category.category_key}
              </button>
            ))}
          </nav>
        </div>

        {/* Translations Table */}
        {selectedCategory && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('admin.translations.key', 'Key')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('admin.translations.default', 'Default (English)')}
                  </th>
                  {availableLanguages.filter(lang => lang.code !== 'en').map((lang) => (
                    <th key={lang.code} className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {lang.native_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {translations.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {item.key_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {item.default_value}
                    </td>
                    {availableLanguages.filter(lang => lang.code !== 'en').map((lang) => {
                      const translation = item.translations?.find(t => t.language_id === lang.id);
                      return (
                        <td key={lang.code} className="px-6 py-4 text-sm">
                          <input
                            type="text"
                            value={translation?.translated_value || ''}
                            onChange={(e) => updateTranslation(item.id, lang.id, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-600 bg-gray-700 text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500"
                            placeholder={item.default_value}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationManager;